import styled, { css } from 'styled-components';
import { COLOURS, isFocused, media, spacingMixin } from '@belong/themes';
import { ISpacingProps, ITheme } from '@belong/types';
import { Icon } from '../../../Icon';
import { ButtonUnstyled } from '../../ButtonUnstyled';
import { AccordionRowVariant } from './AccordionRow.types';

interface IAccordionWrapper extends ISpacingProps {
  variant?: AccordionRowVariant;
  hasBottomBorder?: boolean;
  hasBackground?: string;
  isOpen: boolean;
}

const onHoverOpacity = 'rgba(0, 0, 0, 0.03)';
const borderLeft = ({ isOpen, variant, theme }: IAccordionWrapper & { theme: ITheme }) => {
  let style = '0.3rem transparent';
  switch (variant) {
    case 'slim':
      style = '0.1rem transparent';
      if (isOpen) {
        style = `0.1rem ${theme.borderColorDark}`;
      }

      return style;
    default:
      if (isOpen) {
        style = `0.3rem ${COLOURS.BELONG_BLUE_DARK}`;
      }

      return style;
  }
};

export const Wrapper = styled.div<IAccordionWrapper>`
  ${spacingMixin}
  background-color: ${({ hasBackground, theme }) => hasBackground === 'grey' && theme.backgroundColorDark};
  border-left: solid ${borderLeft};

  &:hover {
    border-left: solid ${borderLeft};
  }

  ${props =>
    props.hasBottomBorder &&
    css`
      border-bottom: 1px solid ${props.theme.borderColor};
    `}
`;

interface IAccordionButton {
  hasBackground?: string;
  hasVerticalBorder?: boolean;
  variant?: AccordionRowVariant;
  available?: boolean;
}

export const Button = styled(ButtonUnstyled).attrs({ type: 'button' })<IAccordionButton>`
  color: ${({ available, theme }) => (available ? theme.foregroundColorDark : theme.greyFontColor)};
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  .accordion-row-inline-icon {
    color: ${({ available, theme }) => !available && theme.greyFontColor};
  }

  &:hover {
    cursor: pointer;
    background-color: ${({ hasBackground, theme }) =>
      hasBackground === 'grey' ? theme.backgroundColorLight : onHoverOpacity};
  }

  &:focus {
    ${isFocused(false, -0.8)};
    background-color: ${({ hasBackground, theme }) =>
      hasBackground === 'grey' ? theme.backgroundColorLight : onHoverOpacity};
  }

  ${({ variant }) => {
    switch (variant) {
      case 'slim':
        return css`
          padding: 1rem 1.2rem;

          ${media('md')`
            padding: 1.2rem;
          `}
        `;

      default:
        return css`
          padding: 2.4rem 1.6rem;
        `;
    }
  }}

  ${props =>
    props.hasVerticalBorder &&
    css`
      border-top: 1px solid ${props.theme.borderColor};
      border-bottom: 1px solid ${props.theme.borderColor};
    `};
`;

export const Body = styled.div<{ variant?: AccordionRowVariant } & ISpacingProps>`
  ${({ variant }) => {
    switch (variant) {
      case 'slim':
        return css`
          padding: 1.2rem 1.2rem;

          ul li:first-child {
            padding-top: 0;
          }
          ul li {
            padding: 0.8rem 0 !important;
          }

          ${media('md')`
            ul li {
              padding: 1.6rem 0;
            }
      `}
        `;

      default:
        return css`
          padding: 2.4rem 1.6rem;
        `;
    }
  }};
`;

export const StyledIconContainer = styled.span`
  display: flex;
  align-items: flex-start;

  ${media('md')`
    padding-right: 1.5rem;
  `}
`;

export const StyledIcon = styled(Icon)`
  color: ${({ theme }) => theme.foregroundColor};
  text-align: center;
  margin-right: 0.8rem;

  ${media('md')`
    margin-right: 1.2rem;
  `}
`;

export const Label = styled.span`
  margin-right: 2rem;

  ${media('md')`
    margin-right: 1.2rem;
  `}
`;
